// src/components/admin/EmployeeManagement.js
import React, { useState, useEffect } from 'react';
import { Routes, Route, useNavigate, useLocation } from 'react-router-dom';
import { Plus, Search, Filter, Download, Upload, Shield, AlertCircle, XCircle, Check, Sparkles, ArrowRight, BarChart3, Target, Rocket } from 'lucide-react';
import { useEmployees } from '../../contexts/EmployeeContext';
import { useTheme } from '../../contexts/ThemeContext';
import functionalStructureService from '../../services/functionalStructure.service';
import EmployeeList from './employees/EmployeeList';
import EmployeeDetails from './employees/EmployeeDetails';
import AddEmployeeWizard from './employees/AddEmployeeWizard';
import BulkImport from './employees/BulkImport';
import EmployeeTestUtilities from './employees/EmployeeTestUtilities';
import EmployeeAnalytics from './employees/EmployeeAnalytics';

const EmployeeManagement = () => {
    const { employees, departments, addEmployee, bulkAddEmployees, getEmployeeAddRestriction, checkEmployeeAddRestriction } = useEmployees();
    const { theme } = useTheme();
    const navigate = useNavigate();

    // Inline styles for full height container
    const containerStyle = {
        height: 'calc(100vh - 80px)', // Adjust 80px for app navbar
        display: 'flex',
        flexDirection: 'column'
    };
    const [loading, setLoading] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const [filterDepartment, setFilterDepartment] = useState('');
    const [showAddModal, setShowAddModal] = useState(false);
    const [showImportModal, setShowImportModal] = useState(false);
    const [showUpgradeModal, setShowUpgradeModal] = useState(false);
    const [showCreateAccessModal, setShowCreateAccessModal] = useState(false);
    const [showPrerequisiteModal, setShowPrerequisiteModal] = useState(false);
    const [activeTab, setActiveTab] = useState('employees');
    const tabLocation = useLocation();

    // Sync activeTab from URL ?tab= param (for sidebar sub-navigation)
    useEffect(() => {
        const params = new URLSearchParams(tabLocation.search);
        const tab = params.get('tab');
        if (tab) setActiveTab(tab);
    }, [tabLocation.search]);

    const [hasLegalEntities, setHasLegalEntities] = useState(false);
    const addOneRestriction = getEmployeeAddRestriction(1);

    // Debug: Log mount/unmount
    useEffect(() => {
        console.log('🔄 EmployeeManagement mounted');
        return () => {
            console.log('❌ EmployeeManagement unmounting');
        };
    }, []);

    // Check for legal entities on component mount
    useEffect(() => {
        checkLegalEntities();
    }, []);

    // Cleanup function to close all modals when unmounting
    useEffect(() => {
        return () => {
            setShowAddModal(false);
            setShowImportModal(false);
            setShowUpgradeModal(false);
            setShowCreateAccessModal(false);
            setShowPrerequisiteModal(false);
        };
    }, []);

    const checkLegalEntities = async () => {
        try {
            const data = await functionalStructureService.getActive();
            if (!data || !data.structure_data) {
                setHasLegalEntities(false);
                setShowPrerequisiteModal(true);
            } else {
                const hasEntities = data.structure_data.legalEntities && data.structure_data.legalEntities.length > 0;
                setHasLegalEntities(hasEntities);
                if (!hasEntities) {
                    setShowPrerequisiteModal(true);
                }
            }
        } catch (e) {
            setHasLegalEntities(false);
            setShowPrerequisiteModal(true);
        }
    };

    const handleAddEmployee = async (employeeData) => {
        try {
            await addEmployee(employeeData);
            setShowAddModal(false);
        } catch (error) {
            console.error('Error adding employee:', error);
            alert(error.message || 'Unable to add employee.');
        }
    };

    const handleOpenAddEmployee = async () => {
        try {
            await checkEmployeeAddRestriction(1);
            setShowAddModal(true);
        } catch (error) {
            setShowUpgradeModal(true);
        }
    };

    const handleOpenBulkImport = async () => {
        try {
            // Ensure there is at least one slot before opening importer
            await checkEmployeeAddRestriction(1);
            setShowImportModal(true);
        } catch (error) {
            setShowUpgradeModal(true);
        }
    };

    const handleBulkImport = async (employeesData) => {
        try {
            await checkEmployeeAddRestriction(employeesData.length);

            const importResult = await bulkAddEmployees(employeesData);
            setShowImportModal(false);
            return importResult;
        } catch (error) {
            console.error('Error importing employees:', error);
            setShowUpgradeModal(true);
            throw error;
        }
    };

    const handleExport = async () => {
        try {
            // TODO: Implement export with local employee data
            //console.log('Export feature coming soon');
        } catch (error) {
            console.error('Error exporting employees:', error);
        }
    };

    const filteredEmployees = employees.filter(emp => {
        const matchesSearch = emp.first_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
            emp.last_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
            emp.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
            emp.employee_code.toLowerCase().includes(searchTerm.toLowerCase());
        const matchesDepartment = !filterDepartment || emp.department_id === filterDepartment;
        return matchesSearch && matchesDepartment;
    });

    return (
        <Routes>
            <Route path="/" element={
                <div className="flex flex-col h-full gap-3">
                    {/* Tabs */}
                    <div className="bg-white dark:bg-gray-800 rounded-lg shadow dark:shadow-gray-900">
                        <div className="border-b dark:border-gray-700">
                            <nav className="flex space-x-8 px-6">
                                <button
                                    onClick={() => setActiveTab('employees')}
                                    className={`py-4 px-2 border-b-2 font-medium text-sm transition-colors ${
                                        activeTab === 'employees'
                                            ? 'border-blue-500 text-blue-600 dark:text-blue-400'
                                            : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'
                                    }`}
                                >
                                    Employees
                                </button>
                                <button
                                    onClick={() => setActiveTab('analytics')}
                                    className={`py-4 px-2 border-b-2 font-medium text-sm transition-colors flex items-center gap-2 ${
                                        activeTab === 'analytics'
                                            ? 'border-blue-500 text-blue-600 dark:text-blue-400'
                                            : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'
                                    }`}
                                >
                                    <BarChart3 className="w-4 h-4" />
                                    Analytics
                                </button>
                                <button
                                    onClick={() => setActiveTab('recruitment')}
                                    className={`py-4 px-2 border-b-2 font-medium text-sm transition-colors flex items-center gap-2 ${
                                        activeTab === 'recruitment'
                                            ? 'border-blue-500 text-blue-600 dark:text-blue-400'
                                            : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'
                                    }`}
                                >
                                    <Target className="w-4 h-4" />
                                    Recruitment
                                </button>
                            </nav>
                        </div>
                    </div>

                    {activeTab === 'analytics' ? (
                        <EmployeeAnalytics />
                    ) : activeTab === 'recruitment' ? (
                        <div className="bg-white dark:bg-gray-800 rounded-xl shadow dark:shadow-gray-900 p-12 text-center">
                            <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-blue-50 dark:bg-blue-900/20 mb-4">
                                <Target className="w-8 h-8 text-blue-500" />
                            </div>
                            <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">Recruitment</h2>
                            <p className="text-gray-500 dark:text-gray-400 max-w-md mx-auto mb-6">
                                Post job openings, track applicants, manage interview pipelines, and streamline your hiring process — all in one place.
                            </p>
                            <div className="flex flex-wrap justify-center gap-3 mb-8">
                                {['Job Postings', 'Applicant Tracking', 'Interview Scheduling', 'Offer Management', 'Candidate Pipeline'].map(f => (
                                    <span key={f} className="px-3 py-1.5 rounded-full text-xs font-medium bg-blue-50 text-blue-600 dark:bg-blue-900/20 dark:text-blue-400">{f}</span>
                                ))}
                            </div>
                            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-amber-50 dark:bg-amber-900/20 text-amber-700 dark:text-amber-400 text-sm">
                                <Rocket className="w-4 h-4" />
                                Coming Soon
                            </div>
                        </div>
                    ) : (<>
                    {/* Compact Header */}
                    <div className="flex justify-between items-center py-1">
                        <h1 className="text-lg font-semibold text-gray-900 dark:text-white">Employee Management</h1>
                        <div className="flex items-center gap-2">
                            <button
                                onClick={handleOpenBulkImport}
                                className={`px-3 py-1.5 rounded-md flex items-center gap-1.5 text-sm ${
                                    'bg-white border border-gray-300 text-gray-700 hover:bg-gray-50 dark:bg-gray-700 dark:border-gray-600 dark:text-gray-200 dark:hover:bg-gray-600'
                                }`}
                                title="Bulk Import"
                            >
                                <Upload className="w-4 h-4" />
                                Bulk Import
                            </button>
                            <button
                                onClick={handleOpenAddEmployee}
                                className={`tour-add-employee-button px-3 py-1.5 rounded-md flex items-center gap-1.5 text-sm ${
                                    addOneRestriction.allowed
                                        ? 'bg-blue-600 text-white hover:bg-blue-700'
                                        : 'bg-amber-100 text-amber-800 hover:bg-amber-200 dark:bg-amber-900/30 dark:text-amber-200'
                                }`}
                                title={addOneRestriction.allowed ? 'Add Employee' : addOneRestriction.message}
                            >
                                <Plus className="w-4 h-4" />
                                Add Employee
                            </button>
                        </div>
                    </div>
                    {addOneRestriction.isTrial ? (
                        <div className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-800 dark:border-amber-800/60 dark:bg-amber-900/20 dark:text-amber-200">
                            Free trial: {employees.length}/{addOneRestriction.limit} employees
                        </div>
                    ) : addOneRestriction.limit && (
                        <div className="rounded-lg border border-blue-200 bg-blue-50 px-3 py-2 text-sm text-blue-800 dark:border-blue-800/60 dark:bg-blue-900/20 dark:text-blue-200">
                            {addOneRestriction.tier?.charAt(0).toUpperCase()}{addOneRestriction.tier?.slice(1)} plan: {employees.length}/{addOneRestriction.limit} employees
                        </div>
                    )}

                    {/* Compact Search and Filters */}
                    <div className="flex gap-3 bg-white dark:bg-gray-800 rounded-lg shadow dark:shadow-gray-900 px-3 py-2">
                        <div className="flex-1 relative">
                            <Search className="absolute left-2.5 top-1/2 transform -translate-y-1/2 text-gray-400 dark:text-gray-500 w-4 h-4" />
                            <input
                                type="text"
                                placeholder="Search..."
                                className="tour-employee-search w-full pl-8 pr-3 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded dark:bg-gray-700 dark:text-white placeholder-gray-400 dark:placeholder-gray-500"
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                            />
                        </div>
                        <select
                            className="tour-employee-department-filter px-3 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded dark:bg-gray-700 dark:text-white"
                            value={filterDepartment}
                            onChange={(e) => setFilterDepartment(e.target.value)}
                        >
                            <option value="">All Departments</option>
                            {departments.map(dept => (
                                <option key={dept.id} value={dept.id}>{dept.name}</option>
                            ))}
                        </select>
                    </div>

                    {/* Development Utilities */}
                    {/*<EmployeeTestUtilities />*/}

                    {/* Employee List */}
                    {loading ? (
                        <div className="flex justify-center items-center h-64">Loading...</div>
                    ) : (
                        <div className="flex-1 min-h-0">
                            <EmployeeList
                                employees={filteredEmployees}
                                departments={departments}
                                showCreateAccessModal={showCreateAccessModal}
                                setShowCreateAccessModal={setShowCreateAccessModal}
                            />
                        </div>
                    )}

                    {/* Add Employee Wizard */}
                    {showAddModal && (
                        <AddEmployeeWizard
                            departments={departments}
                            onClose={() => setShowAddModal(false)}
                            onSubmit={handleAddEmployee}
                        />
                    )}

                    {/* Bulk Import Modal */}
                    {showImportModal && (
                        <BulkImport
                            onClose={() => setShowImportModal(false)}
                            onImport={handleBulkImport}
                        />
                    )}

                    {/* Upgrade Modal - simplified single-plan (Pro) variant.
                        The legacy multi-billing-period modal above is preserved
                        in version history for future use. */}
                    {showUpgradeModal && (
                        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4 animate-fadeIn">
                            <div className={`rounded-2xl shadow-2xl max-w-md w-full overflow-hidden ${
                                theme === 'dark' ? 'bg-gray-800 border border-gray-700' : 'bg-white'
                            }`}>
                                {/* Header */}
                                <div className="relative bg-gradient-to-r from-violet-600 via-purple-600 to-fuchsia-600 p-6 text-white">
                                    <button
                                        onClick={() => setShowUpgradeModal(false)}
                                        className="absolute top-3 right-3 p-1.5 rounded-lg bg-white/10 hover:bg-white/20 text-white/80 hover:text-white transition"
                                    >
                                        <XCircle className="w-5 h-5" />
                                    </button>
                                    <div className="flex items-start gap-3">
                                        <div className="p-2.5 bg-white/20 rounded-xl backdrop-blur-sm">
                                            <Sparkles className="w-6 h-6" />
                                        </div>
                                        <div>
                                            <h2 className="text-xl font-bold">Subscribe to Pro</h2>
                                            <p className="text-sm mt-1 text-white/80">
                                                Your trial supports up to <strong>{addOneRestriction?.limit || 2} employees</strong>. Subscribe to add more.
                                            </p>
                                        </div>
                                    </div>
                                </div>

                                {/* Body */}
                                <div className="p-6 space-y-4">
                                    <div className={`rounded-xl p-4 border ${theme === 'dark' ? 'bg-gray-900/50 border-gray-700' : 'bg-gray-50 border-gray-200'}`}>
                                        <div className="flex items-baseline justify-between">
                                            <p className={`text-sm font-semibold ${theme === 'dark' ? 'text-gray-200' : 'text-gray-700'}`}>
                                                Pro Plan
                                            </p>
                                            <p className={`text-xl font-bold ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>
                                                ₹4,999<span className={`text-xs font-normal ${theme === 'dark' ? 'text-gray-400' : 'text-gray-500'}`}>/mo</span>
                                            </p>
                                        </div>
                                        <p className={`text-xs mt-1 ${theme === 'dark' ? 'text-gray-400' : 'text-gray-500'}`}>
                                            Base for 25 employees · +₹79/emp · +GST
                                        </p>
                                    </div>

                                    <ul className="space-y-2">
                                        {[
                                            'Up to 150 employees',
                                            'All HR features (attendance, leave, payroll, performance)',
                                            '50 AI assistant credits / month',
                                            'Priority support'
                                        ].map((feature, idx) => (
                                            <li key={idx} className="flex items-center gap-2 text-sm">
                                                <Check className={`w-4 h-4 flex-shrink-0 ${theme === 'dark' ? 'text-green-400' : 'text-green-600'}`} />
                                                <span className={theme === 'dark' ? 'text-gray-300' : 'text-gray-700'}>{feature}</span>
                                            </li>
                                        ))}
                                    </ul>

                                    <div className="flex gap-2 pt-2">
                                        <button
                                            onClick={() => setShowUpgradeModal(false)}
                                            className={`flex-1 px-4 py-2.5 rounded-lg border transition ${
                                                theme === 'dark'
                                                    ? 'border-gray-600 text-gray-300 hover:bg-gray-700'
                                                    : 'border-gray-300 text-gray-700 hover:bg-gray-50'
                                            }`}
                                        >
                                            Maybe later
                                        </button>
                                        <button
                                            onClick={() => {
                                                setShowUpgradeModal(false);
                                                navigate('/admin/billing');
                                            }}
                                            className="flex-1 px-4 py-2.5 rounded-lg bg-gradient-to-r from-violet-600 to-purple-600 hover:from-violet-700 hover:to-purple-700 text-white font-medium flex items-center justify-center gap-2 shadow-lg shadow-purple-500/25 transition"
                                        >
                                            Subscribe
                                            <ArrowRight className="w-4 h-4" />
                                        </button>
                                    </div>

                                    <div className={`flex items-center justify-center gap-1.5 text-xs ${theme === 'dark' ? 'text-gray-500' : 'text-gray-500'}`}>
                                        <Shield className="w-3.5 h-3.5" />
                                        Secure payment · Cancel anytime
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}

                    {/* Prerequisite Modal */}
                    {showPrerequisiteModal && !hasLegalEntities && (
                        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
                            <div className={`rounded-xl shadow-2xl max-w-2xl w-full ${
                                theme === 'dark' ? 'bg-gray-800 border border-gray-700' : 'bg-white'
                            }`}>
                                {/* Modal Header */}
                                <div className={`p-6 border-b ${theme === 'dark' ? 'border-gray-700' : 'border-gray-200'}`}>
                                    <div className="flex items-start gap-4">
                                        <div className={`p-3 rounded-full ${
                                            theme === 'dark' ? 'bg-yellow-900/30' : 'bg-yellow-100'
                                        }`}>
                                            <AlertCircle className={`w-8 h-8 ${
                                                theme === 'dark' ? 'text-yellow-500' : 'text-yellow-600'
                                            }`} />
                                        </div>
                                        <div className="flex-1">
                                            <h2 className={`text-2xl font-bold ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>
                                                Setup Required
                                            </h2>
                                            <p className={`text-sm mt-1 ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>
                                                Please configure legal entities before adding employees
                                            </p>
                                        </div>
                                        <button
                                            onClick={() => setShowPrerequisiteModal(false)}
                                            className={`p-2 rounded-lg transition-colors ${
                                                theme === 'dark' ? 'hover:bg-gray-700 text-gray-400' : 'hover:bg-gray-100 text-gray-500'
                                            }`}
                                        >
                                            <XCircle className="w-5 h-5" />
                                        </button>
                                    </div>
                                </div>

                                {/* Modal Body */}
                                <div className="p-6">
                                    <div className={`p-5 rounded-lg border ${
                                        theme === 'dark'
                                            ? 'bg-gray-700/30 border-gray-600'
                                            : 'bg-gray-50 border-gray-200'
                                    }`}>
                                        <div className="flex items-start gap-4">
                                            <div className={`p-2 rounded-lg ${
                                                theme === 'dark' ? 'bg-red-900/30' : 'bg-red-50'
                                            }`}>
                                                <XCircle className={`w-5 h-5 ${
                                                    theme === 'dark' ? 'text-red-500' : 'text-red-600'
                                                }`} />
                                            </div>
                                            <div className="flex-1">
                                                <h3 className={`font-semibold mb-1 ${
                                                    theme === 'dark' ? 'text-white' : 'text-gray-900'
                                                }`}>
                                                    Legal Entities Not Configured
                                                </h3>
                                                <p className={`text-sm mb-3 ${
                                                    theme === 'dark' ? 'text-gray-400' : 'text-gray-600'
                                                }`}>
                                                    You need to set up at least one legal entity in your organization structure before you can add employees. Legal entities are required for payroll processing and compliance.
                                                </p>
                                                <button
                                                    onClick={() => {
                                                        setShowPrerequisiteModal(false);
                                                        navigate('/admin/org-view');
                                                    }}
                                                    className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg text-sm font-medium transition-colors"
                                                >
                                                    Setup Legal Entities
                                                </button>
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                {/* Modal Footer */}
                                <div className={`p-6 border-t ${theme === 'dark' ? 'border-gray-700' : 'border-gray-200'}`}>
                                    <div className="flex items-center justify-between">
                                        <p className={`text-sm ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>
                                            Configure legal entities in Organization View → Functional Structure
                                        </p>
                                        <button
                                            onClick={() => setShowPrerequisiteModal(false)}
                                            className={`px-4 py-2 rounded-lg border transition-colors ${
                                                theme === 'dark'
                                                    ? 'border-gray-600 text-gray-300 hover:bg-gray-700'
                                                    : 'border-gray-300 text-gray-700 hover:bg-gray-50'
                                            }`}
                                        >
                                            I'll do this later
                                        </button>
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}
                    </>)}
                </div>
            } />
            <Route path="/:id" element={<EmployeeDetails />} />
        </Routes>
    );
};

export default EmployeeManagement;
